/**
 * validaciones.js - Funciones de validación centralizadas
 * 
 * Centraliza todas las validaciones de la aplicación para:
 * - Evitar duplicación de código
 * - Mantener consistencia en las validaciones
 * - Facilitar el testing
 * - Mejorar la mantenibilidad
 * -
 */

/**
 * Devuelve el tipo operativo de una solicitud.
 * Las solicitudes de Seguros Mercantil se procesan siempre como "Aguda"
 * independientemente de la etiqueta seleccionada por el usuario.
 */
export const tipoEfectivo = (solicitud) => {
  if (!solicitud) return "Aguda";
  const aseguradora = solicitud.aseguradora || "MyH+";
  if (aseguradora === "Seguros Mercantil") return "Aguda";
  return solicitud.tipo || "Aguda";
};

/**
 * Validar diagnóstico farmacológico
 * 
 * @param {Object} mainDiagnostico - Diagnóstico principal seleccionado
 * @param {Object} subDiagnostico - Subdiagnóstico seleccionado
 * @param {Array} subDiagnosticos - Lista de subdiagnósticos disponibles
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarDiagnostico = (mainDiagnostico, subDiagnostico, subDiagnosticos = []) => {
  const errors = {
    mainDiagnostico: !mainDiagnostico,
    subDiagnostico: subDiagnosticos.length > 0 && !subDiagnostico,
  };

  return {
    isValid: !errors.mainDiagnostico && !errors.subDiagnostico,
    errors,
  };
};

/**
 * Validar facturación
 * 
 * @param {Object} factura - Datos de la factura
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarFacturacion = (factura) => {
  const errors = {
    numeroFactura: !factura.numeroFactura || factura.numeroFactura.trim() === "",
    fecha: !factura.fecha,
    montoTotal: !factura.montoTotal || factura.montoTotal <= 0,
    montoDolar: factura.montoDolar !== undefined && factura.montoDolar < 0,
  };

  return {
    isValid: !Object.values(errors).some((error) => error),
    errors,
  };
};

/**
 * Validar delivery
 * 
 * @param {Object} delivery - Datos del delivery
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarDelivery = (delivery) => {
  const errors = {
    servicioEntrega: !delivery.servicioEntrega || !delivery.servicioEntrega.id,
    fecha: !delivery.fecha,
    costoEntrega: !delivery.costoEntrega || delivery.costoEntrega <= 0,
    distanciaReal: !delivery.distanciaReal || delivery.distanciaReal <= 0,
  };

  return {
    isValid: !Object.values(errors).some((error) => error),
    errors,
  };
};

/**
 * Validar comentario
 * 
 * @param {Object} comentario - Datos del comentario
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarComentario = (comentario) => {
  const errors = {
    mensaje: !comentario.mensaje || comentario.mensaje.trim() === "",
    tipo: !comentario.tipo || !["Enviado", "Recibido"].includes(comentario.tipo),
  };

  return {
    isValid: !errors.mensaje && !errors.tipo,
    errors,
  };
};

/**
 * Validar solicitud
 * 
 * @param {Object} solicitud - Datos de la solicitud
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarSolicitud = (solicitud) => {
  const errors = {
    tipo: !solicitud.tipo || !["Aguda", "Cronica"].includes(solicitud.tipo),
    detalle: !solicitud.detalle || solicitud.detalle.trim() === "",
    diagnostico: !solicitud.diagnostico || solicitud.diagnostico.trim() === "",
    titular: !solicitud.titular || !solicitud.titular.id,
  };

  if (tipoEfectivo(solicitud) === "Cronica") {
    errors.patologia = !solicitud.cronico?.patologia;
    errors.fechaVigencia = !solicitud.cronico?.fechaVigencia;
  }

  return {
    isValid: !Object.values(errors).some((error) => error),
    errors,
  };
};

/**
 * Validar email
 * 
 * @param {string} email - Email a validar
 * @returns {boolean}
 */
export const validarEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validar teléfono venezolano
 * 
 * @param {string} telefono - Teléfono a validar
 * @returns {boolean}
 */
export const validarTelefono = (telefono) => {
  if (!telefono) return false;
  // Formato: 0414-1234567 o 04141234567
  const telefonoRegex = /^0(412|414|424|416|426)\d{7}$/;
  return telefonoRegex.test(telefono.replace(/-/g, ""));
};

/**
 * Validar cédula venezolana
 * 
 * @param {string} cedula - Cédula a validar
 * @returns {boolean}
 */
export const validarCedula = (cedula) => {
  if (!cedula) return false;
  // Formato: V-12345678 o E-12345678 o solo números
  const cedulaRegex = /^[VE]-?\d{7,8}$/i;
  return cedulaRegex.test(cedula);
};

/**
 * Validar RIF venezolano
 * 
 * @param {string} rif - RIF a validar
 * @returns {boolean}
 */
export const validarRIF = (rif) => {
  if (!rif) return false;
  // Formato: J-12345678-9
  const rifRegex = /^[JGVEP]-?\d{8,9}-?\d$/i;
  return rifRegex.test(rif);
};

/**
 * Validar fecha
 * 
 * @param {string|Date} fecha - Fecha a validar
 * @returns {boolean}
 */
export const validarFecha = (fecha) => {
  if (!fecha) return false;
  const date = new Date(fecha);
  return date instanceof Date && !isNaN(date);
};

/**
 * Validar rango de fechas
 * 
 * @param {string|Date} fechaInicio - Fecha de inicio
 * @param {string|Date} fechaFin - Fecha de fin
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarRangoFechas = (fechaInicio, fechaFin) => {
  const errors = {
    fechaInicio: !validarFecha(fechaInicio),
    fechaFin: !validarFecha(fechaFin),
    rango: false,
  };

  if (!errors.fechaInicio && !errors.fechaFin) {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    errors.rango = inicio > fin;
  }

  return {
    isValid: !Object.values(errors).some((error) => error),
    errors,
  };
};

/**
 * Validar monto
 * 
 * @param {number} monto - Monto a validar
 * @param {number} minimo - Monto mínimo permitido (default: 0)
 * @param {number} maximo - Monto máximo permitido (default: Infinity)
 * @returns {boolean}
 */
export const validarMonto = (monto, minimo = 0, maximo = Infinity) => {
  if (typeof monto !== "number" || isNaN(monto)) return false;
  return monto >= minimo && monto <= maximo;
};

/**
 * Validar archivo
 * 
 * @param {File} archivo - Archivo a validar
 * @param {Array} tiposPermitidos - Tipos MIME permitidos
 * @param {number} tamañoMaximo - Tamaño máximo en bytes (default: 5MB)
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validarArchivo = (
  archivo,
  tiposPermitidos = ["image/jpeg", "image/png", "application/pdf"],
  tamañoMaximo = 5 * 1024 * 1024 // 5MB
) => {
  const errors = {
    existe: !archivo,
    tipo: archivo && !tiposPermitidos.includes(archivo.type),
    tamaño: archivo && archivo.size > tamañoMaximo,
  };

  return {
    isValid: !Object.values(errors).some((error) => error),
    errors,
    mensajes: {
      tipo: `Tipo de archivo no permitido. Permitidos: ${tiposPermitidos.join(", ")}`,
      tamaño: `Archivo muy grande. Máximo: ${(tamañoMaximo / 1024 / 1024).toFixed(2)}MB`,
    },
  };
};

/**
 * Sanitizar string (remover caracteres peligrosos)
 * 
 * @param {string} str - String a sanitizar
 * @returns {string}
 */
export const sanitizarString = (str) => {
  if (!str) return "";
  return str
    .replace(/[<>]/g, "") // Remover < y >
    .replace(/javascript:/gi, "") // Remover javascript:
    .replace(/on\w+=/gi, "") // Remover event handlers
    .trim();
};

/**
 * Validar búsqueda (longitud mínima)
 * 
 * @param {string} query - Query de búsqueda
 * @param {number} longitudMinima - Longitud mínima (default: 3)
 * @returns {boolean}
 */
export const validarBusqueda = (query, longitudMinima = 3) => {
  if (!query) return false;
  return query.trim().length >= longitudMinima;
};

export default {
  tipoEfectivo,
  validarDiagnostico,
  validarFacturacion,
  validarDelivery,
  validarComentario,
  validarSolicitud,
  validarEmail,
  validarTelefono,
  validarCedula,
  validarRIF,
  validarFecha,
  validarRangoFechas,
  validarMonto,
  validarArchivo,
  sanitizarString,
  validarBusqueda,
};
